/**
 * Share one in-flight request between callers that ask for exactly the same
 * thing at the same time (e.g. several components mounting together, each
 * loading the organization's features).
 *
 * Nothing is cached: the entry is removed the moment the request settles, so a
 * later call always makes a fresh request. Entries are scoped to an owner
 * object (typically the API client of the signed-in user) so two users or
 * clients never share a response.
 */

const byOwner = new WeakMap();

/**
 * @template T
 * @param {object} owner   scope for the shared entry (an API client instance)
 * @param {string} key     identifies the request (URL + params)
 * @param {() => Promise<T>} run  performs the request when none is in flight
 * @returns {Promise<T>}
 */
export function dedupeInflight(owner, key, run) {
  if (!owner || (typeof owner !== 'object' && typeof owner !== 'function')) return run();
  let pending = byOwner.get(owner);
  if (!pending) {
    pending = new Map();
    byOwner.set(owner, pending);
  }
  const existing = pending.get(key);
  if (existing) return existing;
  const request = Promise.resolve()
    .then(run)
    .finally(() => { pending.delete(key); });
  pending.set(key, request);
  return request;
}

export default dedupeInflight;
